import { where } from 'firebase/firestore'

import FirestoreService from '../../firestoreService'
import { getConvertedData } from '../../../models/interface.helper'

import { RegistrationTicketModel } from '../../../models/registration-tickets/registrationTicket.model'

import IRegistrationTicketService from './types'
import { IRegistrationTicketInterface } from '../../../models/registration-tickets/registrationTicket.interface'

import { CONST } from '../../../const/const'

// Constants
const COLLECTIONS = CONST.DATA.FIRESTORE.LATEST.COLLECTIONS

// %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
/**
 * @info Event payment settings service
 */
class RegistrationTicketService {
  // %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
  /**
   * @info Fetches the doc from the db
   * @param {IRegistrationTicketService["IGetRequest"]} req
   */
  get: IRegistrationTicketService['IGet'] = async (
    req: IRegistrationTicketService['IGetRequest']
  ) => {
    const documentSnapshot = await FirestoreService.getItem(
      COLLECTIONS.REGISTRATION_TICKET.NAME,
      req.docId
    )

    if (!documentSnapshot.exists())
      throw new Error(`RegistrationTicket doc where id: ${req.docId} does not exist`)

    const registrationTicket = RegistrationTicketModel.fromFirestoreDoc(documentSnapshot).toObject()

    return {
      registrationTicket,
    }
  }

  // %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
  /**
   * @info Fetches the doc from the db
   * @param {IRegistrationTicketService["ICreateRequest"]} req
   */
  create: IRegistrationTicketService['ICreate'] = async (
    req: IRegistrationTicketService['ICreateRequest']
  ) => {
    const docToCreate = new RegistrationTicketModel(req.data).toFirestore()

    if (req.id) {
      await FirestoreService.createItemWithCustomId(
        COLLECTIONS.REGISTRATION_TICKET.NAME,
        req.id,
        docToCreate
      )
      return {
        docId: req.id,
        registrationTicket: {
          id: req.id,
          docToCreate,
        },
      }
    } else {
      const documentSnapshot = await FirestoreService.createItem(
        COLLECTIONS.REGISTRATION_TICKET.NAME,
        docToCreate
      )
      return {
        docId: documentSnapshot.id,
        registrationTicket: docToCreate,
      }
    }
  }

  // %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
  /**
   * @info Fetches the doc from the db
   * @param {IRegistrationTicketService["IUpdateRequest"]} req
   */
  update: IRegistrationTicketService['IUpdate'] = async (
    req: IRegistrationTicketService['IUpdateRequest']
  ) => {
    let registrationTicket = await this.get({
      docId: req.docId,
    })

    await FirestoreService.updateItem(
      COLLECTIONS.REGISTRATION_TICKET.NAME,
      req.docId,
      new RegistrationTicketModel({
        ...registrationTicket,
        ...req.fieldsToUpdate,
      }).toFirestore()
    )

    return {
      docId: req.docId,
      updatedFields: req.fieldsToUpdate,
    }
  }

  // %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
  /**
   * @info Fetches the doc from the db
   * @param {IRegistrationTicketService["IDeleteRequest"]} req
   */
  delete: IRegistrationTicketService['IDelete'] = async (
    req: IRegistrationTicketService['IDeleteRequest']
  ) => {
    await FirestoreService.deleteItem(COLLECTIONS.REGISTRATION_TICKET.NAME, req.docId)

    return true
  }

  getByEventId = async (eventId: string) => {
    const eventTicketsInDb: IRegistrationTicketInterface[] = []

    const eventRegisteredUsersSnaps = await FirestoreService.filterItems(
      COLLECTIONS.REGISTRATION_TICKET.NAME,
      [where(COLLECTIONS.REGISTRATION_TICKET.FIELDS.EVENT_ID.KEY, '==', eventId)]
    )

    eventRegisteredUsersSnaps.docs.forEach((currDoc) => {
      eventTicketsInDb.push(
        getConvertedData(RegistrationTicketModel.fromFirestoreDoc(currDoc).toObject())
      )
    })

    return eventTicketsInDb
  }
}

const registrationTicketService = new RegistrationTicketService()

export default registrationTicketService
